import {
  Center,
  Loader,
  Pagination,
  Pill,
  SimpleGrid,
  Table,
  Title,
  Text,
  ActionIcon,
  Badge,
  Modal,
  Stack,
  Space,
  Tooltip,
} from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileArrowDown } from "@fortawesome/sharp-solid-svg-icons";
import {
  faCheck,
  faCircleNotch,
  faXmark,
} from "@fortawesome/sharp-regular-svg-icons";
import { useMutation, useQuery } from "@apollo/client";
import { PropsWithChildren, useState } from "react";
import { format } from "date-fns";
import { gql } from "@/__generated__";
import {
  Maybe,
  ProviderReferralsQuery,
  SponsoringParty,
} from "@/__generated__/graphql";
import InlineBanner from "../shared/InlineBanner";
import CheckboxIndicator from "../CheckboxIndicator";

import styles from "./referral-table.module.scss";

export const GET_REFERRALS = gql(`
  query ProviderReferrals($limit: Int, $offset: Int) { 
    referrals(limit: $limit, offset: $offset) { 
      referrals { 
        referralId
        patientEmail
        appeal { 
          appealId
          referringProviderName
          createdTimestamp
          updatedTimestamp
          sponsoringParty
          sponsorshipExpiredTimestamp
          status { 
            insuranceCardUpload
            insuranceCardUploadSkipped
            paymentSuccessful
            issueOutstanding
            denialUpheld
            denialOverturned
            appealSubmitted
          }
          patient { 
            firstName
            lastName
          }
        }
      }
      totalCount
    }
  }
`);

export const GET_DOWNLOAD_LINK = gql(`
  mutation DownloadAppealLetterLink($appealId: ID!) {
    downloadAppealLetterLink(appealId: $appealId)
  }
`);

export const UPDATE_APPEAL_STATUS = gql(`
  mutation ReportAppealStatus($appealId: ID!, $status: ReportAppealStatusInput!) {
    reportAppealOutcomeStatus(appealId: $appealId, status: $status)
  }
`);

const PAGE_SIZE = 10;

type IndicatorTooltipProps = {
  sponsoringParty: SponsoringParty;
};

const IndicatorTooltip = ({
  sponsoringParty,
  children,
}: PropsWithChildren<IndicatorTooltipProps>) => (
  <Tooltip
    label={`Sponsored by ${sponsoringParty === SponsoringParty.Claimable ? "Claimable" : "your practice"}`}
    withArrow
    arrowSize={16}
  >
    {children}
  </Tooltip>
);

type SponsoredAppealIndicatorProps = {
  sponsoringParty?: Maybe<SponsoringParty>;
  isExpired: boolean;
  isAppealSubmitted: boolean;
};

const SponsoredAppealIndicator = ({
  sponsoringParty,
  isExpired,
  isAppealSubmitted,
}: SponsoredAppealIndicatorProps) => {
  if (!sponsoringParty) {
    return null;
  }

  if (isExpired) {
    return <CheckboxIndicator variant="x" />;
  }

  if (!isAppealSubmitted) {
    return (
      <IndicatorTooltip sponsoringParty={sponsoringParty}>
        <CheckboxIndicator variant="disabled-check" />
      </IndicatorTooltip>
    );
  }

  return (
    <IndicatorTooltip sponsoringParty={sponsoringParty}>
      <CheckboxIndicator variant="check" />
    </IndicatorTooltip>
  );
};

enum AppealState {
  Referred = "Referred",
  InProgress = "In Progress",
  Submitted = "Submitted",
  InSupport = "Support In Progress",
  NotApproved = "Not Approved",
  Approved = "Approved",
  Expired = "Expired",
}

type ReferralTableProps = {
  showTitle?: boolean;
};

const ReferralTable = ({ showTitle }: ReferralTableProps) => {
  const [activePage, setPage] = useState(1);
  const [referralsByPage, setReferralsByPage] = useState<
    ProviderReferralsQuery["referrals"]["referrals"][]
  >([]);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const [appealDownloading, setAppealDownloading] = useState("");
  const [reportAppealStatus, setReportAppealStatus] = useState("");
  const { loading, error, fetchMore, data } = useQuery(GET_REFERRALS, {
    fetchPolicy: "no-cache",
    variables: { limit: PAGE_SIZE, offset: 0 },
    onCompleted: ({ referrals }) => {
      setReferralsByPage([referrals.referrals]);
    },
  });

  const [getDownloadLink] = useMutation(GET_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
  });

  const [
    updateRepealStatus,
    {
      loading: updateRepealStatusLoading,
      error: errorUpdatingRepealStatus,
      reset,
    },
  ] = useMutation(UPDATE_APPEAL_STATUS, {
    onCompleted: async () => {
      const result = await fetchMore({
        variables: {
          offset: (activePage - 1) * PAGE_SIZE,
        },
        updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
      });

      const updatedReferralsByPage = [...referralsByPage];
      updatedReferralsByPage[activePage - 1] = result.data.referrals.referrals;
      setReferralsByPage(updatedReferralsByPage);
      setReportAppealStatus("");
    },
  });

  const downloadAppealPdf = async (
    appeal: ProviderReferralsQuery["referrals"]["referrals"][number]["appeal"],
  ) => {
    setAppealDownloading(appeal.appealId);
    const resp = await getDownloadLink({
      variables: { appealId: appeal.appealId },
    });

    if (resp.data) {
      const pdfResponse = await fetch(
        resp.data.downloadAppealLetterLink as string,
      );

      const pdfBlob = await pdfResponse.blob();
      const downloadAnchor = document.createElement("a");
      const downloadAnchorUrl = URL.createObjectURL(pdfBlob);
      let filename = `${format(new Date(), "MM-dd-yyyy")}-Appeal`;
      if (appeal) {
        filename = `${appeal.patient?.firstName?.charAt(0)}${appeal.patient?.lastName}-${filename}`;
      }
      downloadAnchor.href = downloadAnchorUrl;
      downloadAnchor.download = filename;
      downloadAnchor.target = "_blank";
      downloadAnchor.rel = "noopener noreferrer";

      downloadAnchor.click();

      URL.revokeObjectURL(downloadAnchorUrl);
    }
    setAppealDownloading("");
  };

  const reportStatus = async (status: "approved" | "denied") => {
    if (reportAppealStatus.length === 0) {
      return null;
    }
    if (status === "approved") {
      await updateRepealStatus({
        variables: {
          appealId: reportAppealStatus,
          status: { denialOverturned: true },
        },
      });
    } else {
      await updateRepealStatus({
        variables: {
          appealId: reportAppealStatus,
          status: { denialUpheld: true },
        },
      });
    }

    return null;
  };

  const statusToState = (
    status: ProviderReferralsQuery["referrals"]["referrals"][number]["appeal"]["status"],
    expired: boolean,
  ): AppealState => {
    if (expired) {
      return AppealState.Expired;
    }
    if (status.denialOverturned) {
      return AppealState.Approved;
    }
    if (status.denialUpheld) {
      return AppealState.NotApproved;
    }
    if (status.paymentSuccessful) {
      return AppealState.Submitted;
    }
    if (status.issueOutstanding) {
      return AppealState.InSupport;
    }
    if (status.insuranceCardUpload || status.insuranceCardUploadSkipped) {
      return AppealState.InProgress;
    }

    return AppealState.Referred;
  };

  const appealStateToPill = (state: AppealState) => {
    let rootStyle;
    switch (state) {
      case AppealState.Submitted: {
        rootStyle = styles.submitted;
        break;
      }
      case AppealState.InSupport: {
        rootStyle = styles.support;
        break;
      }
      case AppealState.NotApproved: {
        rootStyle = styles.notApproved;
        break;
      }
      case AppealState.Referred: {
        rootStyle = styles.referred;
        break;
      }
      case AppealState.Expired: {
        rootStyle = styles.expired;
        break;
      }
      case AppealState.Approved: {
        rootStyle = styles.approved;
        break;
      }
      default: {
        rootStyle = styles.inProgress;
        break;
      }
    }

    return (
      <Pill
        classNames={{
          label: styles.statusLabel,
          root: rootStyle,
        }}
      >
        {state.toString()}
      </Pill>
    );
  };

  const downloadButton = (
    appeal: ProviderReferralsQuery["referrals"]["referrals"][number]["appeal"],
    appealState: AppealState,
  ) => {
    if (
      [
        AppealState.Approved,
        AppealState.Submitted,
        AppealState.NotApproved,
      ].includes(appealState)
    ) {
      const isDownloading = appealDownloading === appeal.appealId;
      return (
        <ActionIcon
          variant="transparent"
          className={styles.secondaryActionButton}
          disabled={isDownloading}
          loading={isDownloading}
          onClick={async () => downloadAppealPdf(appeal)}
          type="button"
          aria-label="Download appeal"
        >
          <FontAwesomeIcon
            icon={faFileArrowDown}
            className={styles.download}
            color="#29A7D3"
          />
        </ActionIcon>
      );
    }
    return null;
  };

  const actionPillForState = (appealId: string, appealState: AppealState) => {
    if (appealState === AppealState.Submitted) {
      return (
        <Badge
          fullWidth
          color="black"
          variant="outline"
          classNames={{
            label: styles.statusLabel,
            root: styles.action,
          }}
          onClick={() => setReportAppealStatus(appealId)}
        >
          {"Report Status"}
        </Badge>
      );
    }
    return null;
  };

  const rows = (referrals: ProviderReferralsQuery["referrals"]["referrals"]) =>
    referrals.map((referral) => {
      const appealState: AppealState = statusToState(
        referral.appeal.status,
        Boolean(referral.appeal.sponsorshipExpiredTimestamp),
      );
      return (
        <Table.Tr key={referral.referralId}>
          <Table.Td>
            {
              <SimpleGrid cols={1} verticalSpacing="xs">
                <Text size="14px">{`${referral.appeal.patient?.firstName} ${referral.appeal.patient?.lastName}`}</Text>
                <Text size="12px">{referral.patientEmail}</Text>
              </SimpleGrid>
            }
          </Table.Td>
          <Table.Td>
            {format(new Date(referral.appeal.createdTimestamp), "MM/dd/yyyy")}
          </Table.Td>
          <Table.Td>
            {format(new Date(referral.appeal.updatedTimestamp), "MM/dd/yyyy")}
          </Table.Td>
          <Table.Td align="center">
            <SponsoredAppealIndicator
              sponsoringParty={referral.appeal.sponsoringParty}
              isAppealSubmitted={Boolean(
                referral.appeal.status.appealSubmitted,
              )}
              isExpired={Boolean(referral.appeal.sponsorshipExpiredTimestamp)}
            />
          </Table.Td>
          <Table.Td>{appealStateToPill(appealState)}</Table.Td>
          <Table.Td>
            {actionPillForState(referral.appeal.appealId, appealState)}
          </Table.Td>
          <Table.Td align="right">
            {downloadButton(referral.appeal, appealState)}
          </Table.Td>
        </Table.Tr>
      );
    });

  return (
    <div className={styles.tableContainer}>
      {showTitle && (
        <Title order={1} className={styles.center}>
          {"My Appeals"}
        </Title>
      )}
      {(loading || fetchMoreLoading) && <Loader color="black" />}
      {!loading &&
        !error &&
        !fetchMoreLoading &&
        data &&
        data.referrals.totalCount === 0 &&
        "Start referring patients to track their appeals here"}
      {!loading &&
        !error &&
        !fetchMoreLoading &&
        data &&
        data.referrals.totalCount > 0 &&
        referralsByPage.length > 0 && (
          <Modal
            opened={!!reportAppealStatus}
            onClose={async () => {
              setReportAppealStatus("");
            }}
            radius={16}
            centered
          >
            {errorUpdatingRepealStatus && (
              <InlineBanner
                message="Error while reporting status."
                type="error"
                dismissable
                onDismiss={() => reset()}
              />
            )}
            <Text size="xl">{"What is the status of the appeal?"}</Text>
            <Space h="xl" />
            <Stack>
              <button
                type="button"
                className={styles.buttons}
                onClick={() => reportStatus("approved")}
              >
                <div className={styles.iconContainer}>
                  <FontAwesomeIcon
                    icon={
                      updateRepealStatusLoading || loading
                        ? faCircleNotch
                        : faCheck
                    }
                    pulse={updateRepealStatusLoading}
                    className={styles.icon}
                  />
                </div>
                {"Appeal approved"}
              </button>
              <button
                type="button"
                className={styles.buttons}
                onClick={() => reportStatus("denied")}
              >
                <div className={styles.iconContainer}>
                  <FontAwesomeIcon
                    icon={updateRepealStatusLoading ? faCircleNotch : faXmark}
                    pulse={updateRepealStatusLoading}
                    className={styles.icon}
                  />
                </div>
                {"Appeal not approved"}
              </button>
            </Stack>
            <Space h="lg" />
          </Modal>
        )}
      {!loading && !error && !fetchMoreLoading && (
        <>
          <Table
            highlightOnHover
            classNames={{ table: styles.table, td: styles.td }}
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{"Patient"}</Table.Th>
                <Table.Th>{"Referral Sent"}</Table.Th>
                <Table.Th>{"Last Activity"}</Table.Th>
                <Table.Th className={styles.centerTH}>{"Sponsored"}</Table.Th>
                <Table.Th>{"Appeal Status"}</Table.Th>
                <Table.Th>{"Action"}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            {data &&
              data.referrals.totalCount > 0 &&
              referralsByPage.length > 0 && (
                <Table.Tbody>
                  {rows(referralsByPage[activePage - 1])}
                </Table.Tbody>
              )}
          </Table>
          <Center>
            <Pagination
              size="sm"
              color="black"
              className={styles.pagination}
              value={activePage}
              onChange={async (page) => {
                if (!referralsByPage[page - 1]?.length) {
                  setFetchMoreLoading(true);

                  const result = await fetchMore({
                    variables: {
                      offset: (page - 1) * PAGE_SIZE,
                    },
                    updateQuery: (_, { fetchMoreResult }) => fetchMoreResult,
                  });
                  const updatedReferralsByPage = [...referralsByPage];
                  updatedReferralsByPage[page - 1] =
                    result.data.referrals.referrals;

                  setReferralsByPage(updatedReferralsByPage);
                  setFetchMoreLoading(false);
                }
                setPage(page);
              }}
              total={Math.ceil((data?.referrals.totalCount ?? 0) / PAGE_SIZE)}
            />
          </Center>
        </>
      )}
    </div>
  );
};

export default ReferralTable;
